// Parser for the `lfx2` Additional Layer Information block.
//
// lfx2 is the modern (Photoshop 7+) representation of Layer Styles.  Its
// payload is a single OSType descriptor framed with a 4-byte version.
// The descriptor's class ID is `null` (yes, really, that's an OSType class
// constant) and each effect lives under its own sub-descriptor key:
//
//   DrSh — Drop Shadow            "DropShadow"
//   IrSh — Inner Shadow           "InnerShadow"
//   OrGl — Outer Glow             "OuterGlow"
//   IrGl — Inner Glow             "InnerGlow"
//   ebbl — Bevel & Emboss         "Bevel"
//   ChFX — Satin (Chrome Effect)  "ChromeEffect"
//   SoFi — Color Overlay          "SolidFill"
//   GrFl — Gradient Overlay       "GradientFill"
//   patternFill — Pattern Overlay descriptor key (long form)
//   FrFX — Stroke                 "FrameFX"
//
// Field keys inside each effect descriptor are also documented in the spec
// (and visible in any "Action Manager → ScriptingListener" capture from
// Photoshop).  The set below covers the canonical Layer Style panel.
//
// We do NOT parse the legacy `lrFX` block — return null with a warning so
// callers can decide what to do.

import { BinaryReader, BinaryReaderError } from "./BinaryReader";
import type {
  BevelDirection,
  BevelEmbossEffect,
  BevelStyle,
  BevelTechnique,
  ColorOverlayEffect,
  ContourSpec,
  DropShadowEffect,
  GlowFill,
  GlowTechnique,
  GradientOverlayEffect,
  GradientSpec,
  GradientStyle,
  InnerGlowEffect,
  InnerShadowEffect,
  LayerStyles,
  OuterGlowEffect,
  PatternOverlayEffect,
  PatternRef,
  RGBA,
  SatinEffect,
  StrokeEffect,
  StrokeFillType,
  StrokePosition,
} from "../../document/LayerStyle";
import type { BlendMode } from "../../document/Layer";
import {
  OSTypeDescriptorError,
  asBoolean,
  asDescriptor,
  asDouble,
  asEnumerated,
  asInteger,
  asList,
  asString,
  asUnitFloat,
  getItem,
  readVersionedDescriptor,
  type OSTypeItems,
  type OSTypeValue,
} from "./osTypeDescriptor";

export class LayerEffectsParseError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "LayerEffectsParseError";
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause;
  }
}

/**
 * Parse a raw `lfx2` payload and return a LayerStyles object, or null on
 * empty / invalid input.  Throws only for genuinely malformed descriptors;
 * unknown fields are skipped silently so future Photoshop additions don't
 * break us.
 */
export function parseLayerEffects(
  blockBytes: Uint8Array,
): LayerStyles | null {
  if (!blockBytes || blockBytes.byteLength < 4) return null;
  const reader = new BinaryReader(blockBytes);
  let descriptor: ReturnType<typeof readVersionedDescriptor>["descriptor"];
  try {
    const { descriptor: d } = readVersionedDescriptor(reader);
    descriptor = d;
  } catch (err) {
    if (err instanceof BinaryReaderError || err instanceof OSTypeDescriptorError) {
      return null;
    }
    throw err;
  }

  const styles: Mutable<LayerStyles> = {};

  // Document-level fields (carried inside lfx2 in practice).
  const globalAngle = asDouble(getItem(descriptor, "gagl"));
  if (globalAngle !== undefined) styles.globalAngle = globalAngle;
  const scale = unitPercent(getItem(descriptor, "Scl "));
  if (scale !== undefined) styles.scale = scale;

  // Per-effect dispatch.  Each parser is wrapped so a malformed sub-tree
  // does not poison the whole result.
  trySet(styles, "dropShadow", descriptor, "DrSh", parseDropShadow);
  trySet(styles, "innerShadow", descriptor, "IrSh", parseInnerShadow);
  trySet(styles, "outerGlow", descriptor, "OrGl", parseOuterGlow);
  trySet(styles, "innerGlow", descriptor, "IrGl", parseInnerGlow);
  trySet(styles, "bevelEmboss", descriptor, "ebbl", parseBevelEmboss);
  trySet(styles, "satin", descriptor, "ChFX", parseSatin);
  trySet(styles, "colorOverlay", descriptor, "SoFi", parseColorOverlay);
  trySet(styles, "gradientOverlay", descriptor, "GrFl", parseGradientOverlay);
  trySet(
    styles,
    "patternOverlay",
    descriptor,
    "patternFill",
    parsePatternOverlay,
  );
  trySet(styles, "stroke", descriptor, "FrFX", parseStroke);

  // If nothing was found, treat the block as empty.
  if (Object.keys(styles).length === 0) return null;
  return styles;
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function trySet<K extends keyof LayerStyles>(
  out: Mutable<LayerStyles>,
  field: K,
  parent: OSTypeValue & { kind: "descriptor" },
  key: string,
  parser: (d: OSTypeValue & { kind: "descriptor" }) => LayerStyles[K],
): void {
  const sub = asDescriptor(getItem(parent, key));
  if (!sub) return;
  try {
    const value = parser(sub);
    if (value !== undefined) out[field] = value;
  } catch {
    // Skip individual broken effects rather than failing the whole layer.
  }
}

// -- Common field decoders -----------------------------------------------

/**
 * Blend mode key (`Md  ` enumerated, typeId `BlnM`).  Photoshop uses 4-char
 * keys identical (mostly) to the layer record blend-mode keys.
 */
function readBlendMode(
  d: OSTypeValue & { kind: "descriptor" },
  fallback: BlendMode = "normal",
): BlendMode {
  const e = asEnumerated(getItem(d, "Md  "));
  if (!e) return fallback;
  return blendModeFromKey(e.value) ?? fallback;
}

/**
 * Map Photoshop's descriptor blend-mode enum values to our BlendMode union.
 * The descriptor uses lowercase camelCase keys (`Mltp`, `Scrn`, `Drkn`, …).
 */
function blendModeFromKey(key: string): BlendMode | undefined {
  switch (key) {
    case "Nrml":
      return "normal";
    case "Dslv":
      return "dissolve";
    case "Drkn":
      return "darken";
    case "Mltp":
      return "multiply";
    case "CBrn":
      return "color-burn";
    case "linearBurn":
      return "linear-burn";
    case "darkerColor":
      return "darker-color";
    case "Lghn":
      return "lighten";
    case "Scrn":
      return "screen";
    case "CDdg":
      return "color-dodge";
    case "linearDodge":
      return "linear-dodge";
    case "lighterColor":
      return "lighter-color";
    case "Ovrl":
      return "overlay";
    case "SftL":
      return "soft-light";
    case "HrdL":
      return "hard-light";
    case "vividLight":
      return "vivid-light";
    case "linearLight":
      return "linear-light";
    case "pinLight":
      return "pin-light";
    case "hardMix":
      return "hard-mix";
    case "Dfrn":
      return "difference";
    case "Xclu":
      return "exclusion";
    case "blendSubtraction":
      return "subtract";
    case "blendDivide":
      return "divide";
    case "H   ":
      return "hue";
    case "Strt":
      return "saturation";
    case "Clr ":
      return "color";
    case "Lmns":
      return "luminosity";
    default:
      return undefined;
  }
}

/** Color descriptor (`RGBC` class) → 8-bit RGBA. */
function readColor(
  v: OSTypeValue | undefined,
  fallback: RGBA = { r: 0, g: 0, b: 0, a: 255 },
): RGBA {
  const d = asDescriptor(v);
  if (!d) return fallback;
  const r = clamp8(asDouble(getItem(d, "Rd  ")) ?? fallback.r);
  const g = clamp8(asDouble(getItem(d, "Grn ")) ?? fallback.g);
  const b = clamp8(asDouble(getItem(d, "Bl  ")) ?? fallback.b);
  return { r, g, b, a: 255 };
}

function clamp8(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 255) return 255;
  return Math.round(n);
}

/** Opacity is stored as a UnitFloat with unit `#Prc` in 0..100; we map to 0..1. */
function unitPercent(v: OSTypeValue | undefined): number | undefined {
  const u = asUnitFloat(v);
  if (u === undefined) return undefined;
  return clamp01(u.value / 100);
}

/** Angle is stored as a UnitFloat with unit `#Ang`; we keep degrees. */
function unitAngle(v: OSTypeValue | undefined): number | undefined {
  const u = asUnitFloat(v);
  if (u === undefined) return undefined;
  return u.value;
}

/** Pixel UnitFloat (`#Pxl`) or plain double — we pass the number through. */
function unitPixel(v: OSTypeValue | undefined): number | undefined {
  if (!v) return undefined;
  if (v.kind === "unitFloat") return v.value;
  if (v.kind === "double") return v.value;
  if (v.kind === "integer") return v.value;
  return undefined;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/** Default linear contour. */
const LINEAR_CONTOUR: ContourSpec = Object.freeze({
  points: Object.freeze([
    Object.freeze({ x: 0, y: 0 }),
    Object.freeze({ x: 1, y: 1 }),
  ]),
});

function readContour(v: OSTypeValue | undefined): ContourSpec {
  const d = asDescriptor(v);
  if (!d) return LINEAR_CONTOUR;
  const presetName = asString(getItem(d, "Nm  "));
  // Curve points live in `Crv ` as a list of {Hrzn, Vrtc} descriptors with
  // values in 0..255 (Photoshop's internal contour resolution).
  const list = asList(getItem(d, "Crv "));
  const points: Array<{ x: number; y: number }> = [];
  if (list) {
    for (const it of list) {
      const pd = asDescriptor(it);
      if (!pd) continue;
      const x = asDouble(getItem(pd, "Hrzn")) ?? 0;
      const y = asDouble(getItem(pd, "Vrtc")) ?? 0;
      points.push({ x: clamp01(x / 255), y: clamp01(y / 255) });
    }
  }
  if (points.length === 0) {
    return presetName ? { ...LINEAR_CONTOUR, presetName } : LINEAR_CONTOUR;
  }
  return presetName ? { points, presetName } : { points };
}

function readGradient(v: OSTypeValue | undefined): GradientSpec {
  const d = asDescriptor(v);
  if (!d) {
    return {
      colorStops: [
        { position: 0, color: { r: 0, g: 0, b: 0, a: 255 } },
        { position: 1, color: { r: 255, g: 255, b: 255, a: 255 } },
      ],
      opacityStops: [
        { position: 0, opacity: 1 },
        { position: 1, opacity: 1 },
      ],
      smoothness: 1,
    };
  }
  const presetName = asString(getItem(d, "Nm  "));
  const smoothness = clamp01(
    (asDouble(getItem(d, "Intr")) ?? 4096) / 4096,
  );
  const colorStops: Array<{ position: number; color: RGBA }> = [];
  const opacityStops: Array<{ position: number; opacity: number }> = [];
  const clrs = asList(getItem(d, "Clrs"));
  if (clrs) {
    for (const it of clrs) {
      const sd = asDescriptor(it);
      if (!sd) continue;
      const position = clamp01((asDouble(getItem(sd, "Lctn")) ?? 0) / 4096);
      const color = readColor(getItem(sd, "Clr "));
      colorStops.push({ position, color });
    }
  }
  const trns = asList(getItem(d, "Trns"));
  if (trns) {
    for (const it of trns) {
      const sd = asDescriptor(it);
      if (!sd) continue;
      const position = clamp01((asDouble(getItem(sd, "Lctn")) ?? 0) / 4096);
      const opacity = unitPercent(getItem(sd, "Opct")) ?? 1;
      opacityStops.push({ position, opacity });
    }
  }
  const spec: GradientSpec = {
    colorStops,
    opacityStops,
    smoothness,
    ...(presetName ? { presetName } : {}),
  };
  return spec;
}

function readPatternRef(d: OSTypeValue & { kind: "descriptor" }): PatternRef {
  const id = asString(getItem(d, "Idnt")) ?? "";
  const name = asString(getItem(d, "Nm  "));
  return name !== undefined ? { id, name } : { id };
}

// -- Per-effect parsers ---------------------------------------------------

function parseDropShadow(
  d: OSTypeValue & { kind: "descriptor" },
): DropShadowEffect {
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    color: readColor(getItem(d, "Clr ")),
    opacity: unitPercent(getItem(d, "Opct")) ?? 0.75,
    useGlobalLight: asBoolean(getItem(d, "uglg")) ?? true,
    angle: unitAngle(getItem(d, "lagl")) ?? 120,
    distance: unitPixel(getItem(d, "Dstn")) ?? 0,
    spread: clamp01((asDouble(getItem(d, "Ckmt")) ?? 0) / 100),
    size: unitPixel(getItem(d, "blur")) ?? 5,
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    layerKnocksOut: asBoolean(getItem(d, "layerConceals")) ?? true,
    noise: clamp01((asDouble(getItem(d, "Nose")) ?? 0) / 100),
    contour: readContour(getItem(d, "TrnS")),
  };
}

function parseInnerShadow(
  d: OSTypeValue & { kind: "descriptor" },
): InnerShadowEffect {
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    color: readColor(getItem(d, "Clr ")),
    opacity: unitPercent(getItem(d, "Opct")) ?? 0.75,
    useGlobalLight: asBoolean(getItem(d, "uglg")) ?? true,
    angle: unitAngle(getItem(d, "lagl")) ?? 120,
    distance: unitPixel(getItem(d, "Dstn")) ?? 0,
    choke: clamp01((asDouble(getItem(d, "Ckmt")) ?? 0) / 100),
    size: unitPixel(getItem(d, "blur")) ?? 5,
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    noise: clamp01((asDouble(getItem(d, "Nose")) ?? 0) / 100),
    contour: readContour(getItem(d, "TrnS")),
  };
}

function glowTechniqueFromKey(key: string | undefined): GlowTechnique {
  return key === "PrBL" ? "precise" : "softer";
}

function parseGlowFill(d: OSTypeValue & { kind: "descriptor" }): GlowFill {
  // Gradient glow if a Gradient item is present, otherwise color.
  const gradient = getItem(d, "Grad");
  if (gradient && gradient.kind === "descriptor") {
    return { kind: "gradient", gradient: readGradient(gradient) };
  }
  return { kind: "color", color: readColor(getItem(d, "Clr ")) };
}

function parseOuterGlow(
  d: OSTypeValue & { kind: "descriptor" },
): OuterGlowEffect {
  const technique = glowTechniqueFromKey(
    asEnumerated(getItem(d, "GlwT"))?.value,
  );
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    opacity: unitPercent(getItem(d, "Opct")) ?? 0.75,
    noise: clamp01((asDouble(getItem(d, "Nose")) ?? 0) / 100),
    fill: parseGlowFill(d),
    technique,
    spread: clamp01((asDouble(getItem(d, "Ckmt")) ?? 0) / 100),
    size: unitPixel(getItem(d, "blur")) ?? 5,
    contour: readContour(getItem(d, "TrnS")),
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    range: clamp01((asDouble(getItem(d, "Inpr")) ?? 50) / 100),
    jitter: clamp01((asDouble(getItem(d, "ShdN")) ?? 0) / 100),
  };
}

function parseInnerGlow(
  d: OSTypeValue & { kind: "descriptor" },
): InnerGlowEffect {
  const technique = glowTechniqueFromKey(
    asEnumerated(getItem(d, "GlwT"))?.value,
  );
  const srcKey = asEnumerated(getItem(d, "glwS"))?.value;
  const source: "center" | "edge" = srcKey === "SrcC" ? "center" : "edge";
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    opacity: unitPercent(getItem(d, "Opct")) ?? 0.75,
    noise: clamp01((asDouble(getItem(d, "Nose")) ?? 0) / 100),
    fill: parseGlowFill(d),
    technique,
    source,
    choke: clamp01((asDouble(getItem(d, "Ckmt")) ?? 0) / 100),
    size: unitPixel(getItem(d, "blur")) ?? 5,
    contour: readContour(getItem(d, "TrnS")),
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    range: clamp01((asDouble(getItem(d, "Inpr")) ?? 50) / 100),
    jitter: clamp01((asDouble(getItem(d, "ShdN")) ?? 0) / 100),
  };
}

function bevelStyleFromKey(key: string | undefined): BevelStyle {
  switch (key) {
    case "OtrB":
      return "outer-bevel";
    case "InrB":
      return "inner-bevel";
    case "Embs":
      return "emboss";
    case "PlEb":
      return "pillow-emboss";
    case "strokeEmboss":
      return "stroke-emboss";
    default:
      return "inner-bevel";
  }
}

function bevelTechniqueFromKey(key: string | undefined): BevelTechnique {
  switch (key) {
    case "PrBL":
      return "chisel-hard";
    case "Slmt":
      return "chisel-soft";
    default:
      return "smooth";
  }
}

function bevelDirectionFromKey(key: string | undefined): BevelDirection {
  return key === "Out " ? "up" : key === "In  " ? "down" : "up";
}

function parseBevelEmboss(
  d: OSTypeValue & { kind: "descriptor" },
): BevelEmbossEffect {
  const style = bevelStyleFromKey(asEnumerated(getItem(d, "bvlS"))?.value);
  const technique = bevelTechniqueFromKey(
    asEnumerated(getItem(d, "bvlT"))?.value,
  );
  const direction = bevelDirectionFromKey(
    asEnumerated(getItem(d, "bvlD"))?.value,
  );
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    style,
    technique,
    depth: clamp01((asDouble(getItem(d, "srgR")) ?? 100) / 1000),
    direction,
    size: unitPixel(getItem(d, "blur")) ?? 5,
    soften: unitPixel(getItem(d, "Sftn")) ?? 0,
    useGlobalLight: asBoolean(getItem(d, "uglg")) ?? true,
    angle: unitAngle(getItem(d, "lagl")) ?? 120,
    altitude: unitAngle(getItem(d, "Lald")) ?? 30,
    glossContour: readContour(getItem(d, "TrnS")),
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    highlightBlendMode: readBlendModeKeyed(d, "hglM", "screen"),
    highlightColor: readColor(getItem(d, "hglC"), {
      r: 255,
      g: 255,
      b: 255,
      a: 255,
    }),
    highlightOpacity: unitPercent(getItem(d, "hglO")) ?? 0.75,
    shadowBlendMode: readBlendModeKeyed(d, "sdwM", "multiply"),
    shadowColor: readColor(getItem(d, "sdwC")),
    shadowOpacity: unitPercent(getItem(d, "sdwO")) ?? 0.75,
  };
}

function readBlendModeKeyed(
  d: OSTypeValue & { kind: "descriptor" },
  key: string,
  fallback: BlendMode,
): BlendMode {
  const e = asEnumerated(getItem(d, key));
  if (!e) return fallback;
  return blendModeFromKey(e.value) ?? fallback;
}

function parseSatin(d: OSTypeValue & { kind: "descriptor" }): SatinEffect {
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d, "multiply"),
    color: readColor(getItem(d, "Clr ")),
    opacity: unitPercent(getItem(d, "Opct")) ?? 0.5,
    angle: unitAngle(getItem(d, "lagl")) ?? 19,
    distance: unitPixel(getItem(d, "Dstn")) ?? 11,
    size: unitPixel(getItem(d, "blur")) ?? 14,
    contour: readContour(getItem(d, "MpgS")),
    antiAliased: asBoolean(getItem(d, "AntA")) ?? false,
    invert: asBoolean(getItem(d, "Invr")) ?? false,
  };
}

function parseColorOverlay(
  d: OSTypeValue & { kind: "descriptor" },
): ColorOverlayEffect {
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    color: readColor(getItem(d, "Clr ")),
    opacity: unitPercent(getItem(d, "Opct")) ?? 1,
  };
}

function gradientStyleFromKey(key: string | undefined): GradientStyle {
  switch (key) {
    case "Lnr ":
      return "linear";
    case "Rdl ":
      return "radial";
    case "Angl":
      return "angle";
    case "Rflc":
      return "reflected";
    case "Dmnd":
      return "diamond";
    default:
      return "linear";
  }
}

function parseGradientOverlay(
  d: OSTypeValue & { kind: "descriptor" },
): GradientOverlayEffect {
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    opacity: unitPercent(getItem(d, "Opct")) ?? 1,
    gradient: readGradient(getItem(d, "Grad")),
    reverse: asBoolean(getItem(d, "Rvrs")) ?? false,
    style: gradientStyleFromKey(asEnumerated(getItem(d, "Type"))?.value),
    alignWithLayer: asBoolean(getItem(d, "Algn")) ?? true,
    angle: unitAngle(getItem(d, "Angl")) ?? 90,
    scale: clamp01((asDouble(getItem(d, "Scl ")) ?? 100) / 100),
  };
}

function parsePatternOverlay(
  d: OSTypeValue & { kind: "descriptor" },
): PatternOverlayEffect {
  const patternDesc = asDescriptor(getItem(d, "Ptrn"));
  const pattern: PatternRef = patternDesc
    ? readPatternRef(patternDesc)
    : { id: "" };
  return {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    blendMode: readBlendMode(d),
    opacity: unitPercent(getItem(d, "Opct")) ?? 1,
    pattern,
    scale: clamp01((asDouble(getItem(d, "Scl ")) ?? 100) / 100),
    linkWithLayer: asBoolean(getItem(d, "Lnkd")) ?? true,
    phase: readPhase(getItem(d, "phase")),
  };
}

function readPhase(v: OSTypeValue | undefined): { x: number; y: number } {
  const d = asDescriptor(v);
  if (!d) return { x: 0, y: 0 };
  return {
    x: asDouble(getItem(d, "Hrzn")) ?? 0,
    y: asDouble(getItem(d, "Vrtc")) ?? 0,
  };
}

function strokePositionFromKey(key: string | undefined): StrokePosition {
  switch (key) {
    case "OutF":
      return "outside";
    case "InsF":
      return "inside";
    case "CtrF":
      return "center";
    default:
      return "outside";
  }
}

function strokeFillTypeFromKey(key: string | undefined): StrokeFillType {
  switch (key) {
    case "SClr":
      return "color";
    case "GrFl":
      return "gradient";
    case "PtrF":
      return "pattern";
    default:
      return "color";
  }
}

function parseStroke(d: OSTypeValue & { kind: "descriptor" }): StrokeEffect {
  const fillType = strokeFillTypeFromKey(
    asEnumerated(getItem(d, "PntT"))?.value,
  );
  const result: Mutable<StrokeEffect> = {
    enabled: asBoolean(getItem(d, "enab")) ?? true,
    size: unitPixel(getItem(d, "Sz  ")) ?? 3,
    position: strokePositionFromKey(asEnumerated(getItem(d, "Styl"))?.value),
    blendMode: readBlendMode(d),
    opacity: unitPercent(getItem(d, "Opct")) ?? 1,
    fillType,
  };
  if (fillType === "color") {
    result.color = readColor(getItem(d, "Clr "));
  } else if (fillType === "gradient") {
    result.gradient = readGradient(getItem(d, "Grad"));
    result.gradientReverse = asBoolean(getItem(d, "Rvrs")) ?? false;
    result.gradientStyle = gradientStyleFromKey(
      asEnumerated(getItem(d, "Type"))?.value,
    );
    result.gradientAlignWithLayer = asBoolean(getItem(d, "Algn")) ?? true;
    result.gradientAngle = unitAngle(getItem(d, "Angl")) ?? 0;
    result.gradientScale = clamp01(
      (asDouble(getItem(d, "Scl ")) ?? 100) / 100,
    );
  } else {
    const patternDesc = asDescriptor(getItem(d, "Ptrn"));
    if (patternDesc) result.pattern = readPatternRef(patternDesc);
    result.patternScale = clamp01(
      (asDouble(getItem(d, "Scl ")) ?? 100) / 100,
    );
    result.patternLinkWithLayer = asBoolean(getItem(d, "Lnkd")) ?? true;
    result.patternPhase = readPhase(getItem(d, "phase"));
  }
  return result;
}

// -- Re-exports for callers that want OSType helpers in tests -------------

export type { OSTypeItems, OSTypeValue };
